import classNames from 'classnames';
import { memo, useCallback, useMemo } from 'react';
import { playCardClickSound } from '@wg/web2clientapi/sound';
import { Checkbox, IconTable, TableHead } from '@wg/wows-react-uikit';

import { BATTLE_TYPES } from '~/constants';
import { isChinese } from '~/helpers/realm';

import styles from './MembersTableHead.scss';
import headers from './headers.settings';
import TableHeadCell from '../Table/TableHeadCell';

import type { IconTableTypes } from '@wg/wows-react-uikit/components/IconTable/IconTable';

const HIDDEN_IN_CVC = ['fragsPerBattle', 'damagePerBattle', 'expPerBattle'];
const HIDDEN_SORTING_NAMES = headers()
  .filter((item) => item.isHiddenSortingEnabled)
  .map((item) => item.name);

type SortState = {
  field: string;
  isAsc: boolean;
};

type HeaderItem = ReturnType<typeof headers>[number];

interface MembersTableHeadProps {
  allMembersIsChecked: boolean;
  allMembersIsDisabled: boolean;

  currentBattleType?: BATTLE_TYPES;

  displayCheckboxes?: boolean;
  isActiveSticky?: boolean;
  isMember?: boolean;

  sort: SortState;

  stickyAppContainerId?: string;
  stickyContainerId: string;
  stickyScrollContainerId?: string;

  onThSortClick: (field: string, isAsc: boolean, hiddenSortingNames: string[]) => void;
  onTickAllMembers: () => void;
}

const MembersTableHead = ({
  allMembersIsChecked,
  allMembersIsDisabled,

  currentBattleType,

  displayCheckboxes = false,
  isActiveSticky = true,
  isMember = false,

  sort,

  stickyAppContainerId,
  stickyContainerId,
  stickyScrollContainerId,

  onThSortClick,
  onTickAllMembers,
}: MembersTableHeadProps) => {
  const headerItems = useMemo(() => headers(currentBattleType), [currentBattleType]);

  const handleHeaderClick = useCallback(
    (e: React.MouseEvent, item: HeaderItem) => {
      e.preventDefault();

      if (!item.isSortable) {
        return;
      }

      void playCardClickSound();

      const isAsc = item.name === sort.field ? !sort.isAsc : item.sortDefaultIsAsc;
      onThSortClick(item.name, isAsc, HIDDEN_SORTING_NAMES);
    },
    [onThSortClick, sort.field, sort.isAsc],
  );

  const checkboxCell = displayCheckboxes ? (
    <TableHeadCell modify="check">
      <Checkbox
        onChange={onTickAllMembers}
        isChecked={allMembersIsChecked && !allMembersIsDisabled}
        isDisabled={allMembersIsDisabled}
      />
    </TableHeadCell>
  ) : null;

  return (
    <TableHead
      stickyContainerId={stickyContainerId}
      stickyAppContainerId={stickyAppContainerId}
      stickyScrollContainerId={stickyScrollContainerId}
      isActiveSticky={isActiveSticky}
    >
      <>
        {checkboxCell}

        {headerItems.map((item, index) => {
          if (item.isHidden || (item.forOwnClan && !isMember)) {
            return null;
          }

          const isHiddenInCvc = currentBattleType === BATTLE_TYPES.REGULAR_CVC && HIDDEN_IN_CVC.includes(item.name);

          const canShowText = Boolean(item.text) && (!item.glyph || !isChinese());
          const itemText = canShowText ? <span className={item.glyph ? styles.itemText : ''}>{item.text}</span> : null;

          const icon = item.glyph ? (
            <IconTable
              className={classNames(styles.itemIcon, {
                [styles.itemIconOnly]: !itemText,
              })}
              glyph={item.glyph as IconTableTypes}
            />
          ) : null;

          return (
            <TableHeadCell
              key={index}
              modify={item.modify}
              isSortable={item.isSortable}
              isActive={item.name === sort.field}
              isHidden={isHiddenInCvc}
              onClick={((e: React.MouseEvent) => handleHeaderClick(e, item)) as () => void}
              tooltipText={item.tooltipText}
              tooltipParam={!isChinese() ? item.tooltipParam : ''}
            >
              {icon}
              {itemText}
            </TableHeadCell>
          );
        })}
      </>
    </TableHead>
  );
};

export default memo(MembersTableHead);
